import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import type { PickingListItem, ReportShortageInput, ShortageReason } from '../../types';

const shortageReasons: { value: ShortageReason; label: string }[] = [
  { value: 'not_found', label: 'Item not found at location' },
  { value: 'damaged', label: 'Item is damaged' },
  { value: 'reserved', label: 'Item is reserved for another order' },
  { value: 'wrong_location', label: 'Item at wrong location' },
  { value: 'count_error', label: 'Inventory count error' },
  { value: 'other', label: 'Other reason' },
];

interface ReportShortageModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ReportShortageInput) => Promise<void>;
  item: PickingListItem | null;
  isLoading?: boolean;
}

export function ReportShortageModal({
  open,
  onClose,
  onSubmit,
  item,
  isLoading,
}: ReportShortageModalProps) {
  const [reason, setReason] = useState<ShortageReason>('not_found');
  const [notes, setNotes] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!item) return;

    try {
      await onSubmit({
        shortage_reason: reason,
        notes: notes || undefined,
      });
      toast.success('Shortage reported successfully');
      setReason('not_found');
      setNotes('');
      onClose();
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Unknown error occurred';
      toast.error(`Failed to report shortage: ${message}`);
      console.error('Error reporting shortage:', error);
      // Keep form open for retry
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Report Shortage</DialogTitle>
          <DialogDescription>
            Report that this item cannot be picked from the specified location.
          </DialogDescription>
        </DialogHeader>

        {item && (
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">SKU:</span>
              <span className="font-medium">{item.sku_code}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Serial:</span>
              <span className="font-mono text-sm">{item.serial_numbers?.join(', ') || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Location:</span>
              <span className="font-mono text-sm">{item.bin_code || '-'}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium">Reason *</label>
            <Select value={reason} onValueChange={(v) => setReason(v as ShortageReason)}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select reason" />
              </SelectTrigger>
              <SelectContent>
                {shortageReasons.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium">
              Notes {reason === 'other' ? '*' : '(optional)'}
            </label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Describe the issue..."
              className="mt-1"
              rows={3}
              required={reason === 'other'}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={isLoading || (reason === 'other' && !notes)}
            >
              {isLoading ? 'Reporting...' : 'Report Shortage'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
